import React from "react";
import { Link } from "react-router-dom";
import { Shield, FileText, AlertTriangle, CheckCircle2 } from "lucide-react";

/** Renter-facing trip insurance coverage details (no premiums or rating math). */
export default function Coverage() {
  return (
    <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-10 py-16" data-testid="coverage-page">
      <div className="eyebrow mb-3">Trip protection</div>
      <h1 className="zc-headline text-4xl sm:text-5xl md:text-6xl tracking-tight">
        Insurance coverage included with your rental
      </h1>
      <p className="text-[var(--zc-text-2)] mt-4 max-w-2xl text-base">
        Every confirmed Z-Cars vehicle trip includes per-trip protection for the rental period.
        Coverage details vary by region. British Columbia trips use Z-Cover BC aligned with ICBC
        peer-to-peer (P2P) programs.
      </p>

      <div className="grid md:grid-cols-2 gap-4 mt-12">
        <div className="zc-card p-5">
          <div className="flex items-center gap-2 text-[var(--zc-accent)]">
            <Shield size={20} />
            <div className="zc-title text-lg">What&apos;s covered</div>
          </div>
          <ul className="mt-4 space-y-2 text-sm text-[var(--zc-text)]">
            {[
              "Third-party liability for bodily injury and property damage to others during the trip",
              "Collision / physical damage protection for the host vehicle subject to the deductible",
              "Coverage active from confirmed pick-up through return hand-back",
            ].map((line) => (
              <li key={line} className="flex items-start gap-2">
                <CheckCircle2 size={16} className="text-[var(--zc-success)] mt-0.5 shrink-0" />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="zc-card p-5">
          <div className="flex items-center gap-2 text-[var(--zc-accent)]">
            <FileText size={20} />
            <div className="zc-title text-lg">British Columbia (ICBC)</div>
          </div>
          <ul className="mt-4 space-y-2 text-sm text-[var(--zc-text)]">
            {[
              "Z-Cover BC provides Basic / Optional-style trip protection for P2P rentals in BC",
              "Coverage up to $75,000 per incident with a $1,500 deductible (unless your policy card states otherwise)",
              "Rated using the vehicle’s ICBC rating territory and applicable P2P tariff tables",
              "Coverage region is British Columbia unless your booking policy states a wider area",
            ].map((line) => (
              <li key={line} className="flex items-start gap-2">
                <CheckCircle2 size={16} className="text-[var(--zc-success)] mt-0.5 shrink-0" />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <section className="mt-16">
        <div className="eyebrow mb-3">Other regions</div>
        <h2 className="zc-headline text-3xl md:text-4xl">Default trip protection</h2>
        <p className="text-[var(--zc-text-2)] mt-4 max-w-2xl text-base leading-relaxed">
          Outside BC, trips use the default Z-Cover plan (typically up to $50,000 coverage with a
          $500 deductible) until local programs launch. Limits on your issued policy card control
          if they differ.
        </p>
      </section>

      <section className="mt-16">
        <div className="eyebrow mb-3">Renter responsibilities</div>
        <h2 className="zc-headline text-3xl md:text-4xl">Keep coverage valid</h2>
        <ul className="grid md:grid-cols-2 gap-3 mt-8">
          {[
            "Only verified, approved drivers named on the booking may operate the vehicle",
            "Follow the host’s listing rules, local traffic laws, and return the car on time",
            "Do not drive off-road, race, or operate under the influence — coverage can be voided",
            "Stay within the listed coverage region for the trip",
            "Complete pick-up and return inspections (photos, odometer, fuel) as prompted",
            "Report incidents in-app within 24 hours with photos and details",
          ].map((line) => (
            <li key={line} className="flex items-start gap-3 text-sm text-[var(--zc-text)]">
              <CheckCircle2 size={16} className="text-[var(--zc-success)] mt-0.5 shrink-0" />
              <span>{line}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="mt-16">
        <div className="eyebrow mb-3">Exclusions &amp; deductibles</div>
        <h2 className="zc-headline text-3xl md:text-4xl">What you may still owe</h2>
        <div className="zc-card p-5 mt-6 flex items-start gap-4" style={{ background: "rgba(245,158,11,0.06)", borderColor: "rgba(245,158,11,0.3)" }}>
          <AlertTriangle size={20} className="text-[var(--zc-warn)] shrink-0 mt-0.5" />
          <div className="text-sm text-[var(--zc-text)] leading-relaxed space-y-2">
            <p>
              Deductibles, excluded uses (e.g. unauthorized drivers, intoxication, off-road),
              interior neglect, and charges outside coverage (fuel, mileage overage, late return)
              remain the renter’s responsibility and may be settled from your security deposit
              or billed separately.
            </p>
            <p>
              This page is a summary for renters. The issued policy record for your booking and
              our Terms govern if they conflict. See also{" "}
              <Link to="/safety" className="text-[var(--zc-accent)] hover:underline">Safety</Link>
              {" "}and{" "}
              <Link to="/terms" className="text-[var(--zc-accent)] hover:underline">Terms</Link>.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
